import Phaser from 'phaser';

/** Styles de pion « voiture » (vue du dessus) — 8 variantes ludiques, dont 3 inspirées du web */
export type TokenCarStyle =
  | 'ludo' // Rond avec visage (yeux + sourire) — inspiration Ludo / plateau
  | 'racing-stripe' // Voiture avec bande centrale — inspiration SVG Repo / livrée
  | 'sheriff-star' // Étoile à 5 branches — inspiration badges / western
  | 'bolt' // Éclair
  | 'shield' // Bouclier
  | 'rocket' // Fusée (nez pointu, ailerons)
  | 'heart' // Cœur
  | 'gem'; // Gemme facettée

const HALF = 11; // demi-taille du dessin vectoriel (référence ~22 px)
export const GRAPHICS_TOKEN_DIAMETER = HALF * 2;
/** Rayon de hit (coords locales) pour les pions vectoriels interactifs. */
export const GRAPHICS_TOKEN_LOCAL_HIT_RADIUS = HALF * 0.95;

/** Zone de survol calée sur le cadre texture (sprite) ou le disque du pion vectoriel. */
export function applyTokenHitArea(
  token: Phaser.GameObjects.Sprite | Phaser.GameObjects.Graphics
): void {
  if (token instanceof Phaser.GameObjects.Sprite) {
    const scaleX = Math.abs(token.scaleX) || 1;
    const scaleY = Math.abs(token.scaleY) || 1;
    const w = token.displayWidth / scaleX;
    const h = token.displayHeight / scaleY;
    token.setInteractive(
      new Phaser.Geom.Rectangle(-w * 0.5, -h * 0.5, w, h),
      (rect: Phaser.Geom.Rectangle, x: number, y: number) =>
        Phaser.Geom.Rectangle.Contains(rect, x, y)
    );
  } else {
    token.setInteractive(
      new Phaser.Geom.Circle(0, 0, GRAPHICS_TOKEN_LOCAL_HIT_RADIUS),
      (circle: Phaser.Geom.Circle, x: number, y: number) =>
        Phaser.Geom.Circle.Contains(circle, x, y)
    );
  }
  if (token.input) token.input.cursor = 'pointer';
}

/**
 * Crée un pion « voiture » en Graphics (forme + couleur joueur).
 * @param displaySize diamètre cible en unités monde (lié à la taille de case).
 */
export function createCarToken(
  scene: Phaser.Scene,
  x: number,
  y: number,
  color: number,
  style: TokenCarStyle,
  displaySize: number
): Phaser.GameObjects.Graphics {
  const g = scene.add.graphics();
  g.setPosition(x, y);
  g.setDepth(10);
  g.setScale(displaySize / GRAPHICS_TOKEN_DIAMETER);

  const outline = 0x1e293b;
  const fill = (c: number, alpha = 1) => g.fillStyle(c, alpha);
  const stroke = (c: number, width = 1.5, alpha = 1) => g.lineStyle(width, c, alpha);

  switch (style) {
    case 'ludo': {
      // Rond avec visage (yeux + sourire) — inspiration Ludo
      const r = HALF * 0.95;
      fill(color);
      g.fillCircle(0, 0, r);
      stroke(outline, 1.5, 1);
      g.strokeCircle(0, 0, r);
      fill(0x0f172a);
      g.fillCircle(-r * 0.3, -r * 0.15, r * 0.2);
      g.fillCircle(r * 0.3, -r * 0.15, r * 0.2);
      stroke(0x0f172a, 1.8, 1);
      g.beginPath();
      g.arc(0, r * 0.2, r * 0.4, Phaser.Math.DegToRad(-140), Phaser.Math.DegToRad(-40), false);
      g.strokePath();
      break;
    }
    case 'racing-stripe': {
      // Voiture avec bande centrale — inspiration livrée course
      const w = HALF * 0.6;
      const len = HALF * 1.1;
      fill(color);
      g.fillRoundedRect(-w, -len, w * 2, len * 2, 5);
      fill(0x0f172a);
      g.fillRect(-w * 0.25, -len, w * 0.5, len * 2);
      stroke(outline, 1.5, 1);
      g.strokeRoundedRect(-w, -len, w * 2, len * 2, 5);
      stroke(color, 1, 0.9);
      g.strokeRoundedRect(-w, -len, w * 2, len * 2, 5);
      break;
    }
    case 'sheriff-star': {
      // Étoile à 5 branches — inspiration badge shérif
      const outer = HALF * 0.95;
      const inner = HALF * 0.4;
      const pts: { x: number; y: number }[] = [];
      for (let i = 0; i < 10; i++) {
        const radius = i % 2 === 0 ? outer : inner;
        const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
        pts.push({ x: Math.cos(a) * radius, y: Math.sin(a) * radius });
      }
      fill(color);
      g.beginPath();
      g.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
      g.closePath();
      g.fillPath();
      stroke(outline, 1.5, 1);
      g.beginPath();
      g.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
      g.closePath();
      g.strokePath();
      stroke(color, 1, 0.85);
      g.beginPath();
      g.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
      g.closePath();
      g.strokePath();
      break;
    }
    case 'bolt': {
      // Éclair (zigzag)
      const w = HALF * 0.5;
      const h = HALF * 1.0;
      const pts = [
        { x: 0, y: -h },
        { x: w * 0.6, y: 0 },
        { x: -w * 0.3, y: 0 },
        { x: w * 0.5, y: h },
        { x: 0, y: h * 0.4 },
        { x: -w * 0.5, y: -h * 0.3 },
        { x: 0, y: -h },
      ];
      fill(color);
      g.beginPath();
      g.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
      g.closePath();
      g.fillPath();
      stroke(outline, 1.5, 1);
      g.beginPath();
      g.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
      g.closePath();
      g.strokePath();
      stroke(color, 1, 0.9);
      g.beginPath();
      g.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
      g.closePath();
      g.strokePath();
      break;
    }
    case 'shield': {
      // Bouclier (base arrondie, pointe en bas)
      const w = HALF * 0.85;
      const h = HALF * 1.05;
      const pts = [
        { x: 0, y: -h * 0.9 },
        { x: w, y: -h * 0.2 },
        { x: w * 0.85, y: h * 0.7 },
        { x: 0, y: h },
        { x: -w * 0.85, y: h * 0.7 },
        { x: -w, y: -h * 0.2 },
      ];
      fill(color);
      g.beginPath();
      g.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
      g.closePath();
      g.fillPath();
      stroke(outline, 1.5, 1);
      g.beginPath();
      g.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
      g.closePath();
      g.strokePath();
      stroke(color, 1, 0.9);
      g.beginPath();
      g.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
      g.closePath();
      g.strokePath();
      break;
    }
    case 'rocket': {
      // Fusée (nez pointu, ailerons arrière)
      const w = HALF * 0.55;
      const len = HALF * 1.1;
      fill(color);
      g.fillTriangle(0, -len, -w, len * 0.7, w, len * 0.7);
      fill(0x0f172a);
      g.fillTriangle(-w * 0.7, len * 0.5, -w, len * 0.95, -w * 0.3, len * 0.7);
      g.fillTriangle(w * 0.7, len * 0.5, w, len * 0.95, w * 0.3, len * 0.7);
      stroke(outline, 1.5, 1);
      g.strokeTriangle(0, -len, -w, len * 0.7, w, len * 0.7);
      stroke(color, 1, 0.9);
      g.strokeTriangle(0, -len, -w, len * 0.7, w, len * 0.7);
      break;
    }
    case 'heart': {
      // Cœur (2 demi-cercles + pointe)
      const scale = HALF * 0.9;
      fill(color);
      g.fillCircle(-scale * 0.5, -scale * 0.2, scale * 0.55);
      g.fillCircle(scale * 0.5, -scale * 0.2, scale * 0.55);
      g.fillTriangle(-scale * 1.05, scale * 0.15, scale * 1.05, scale * 0.15, 0, scale * 1.05);
      stroke(outline, 1.5, 1);
      g.strokeCircle(-scale * 0.5, -scale * 0.2, scale * 0.55);
      g.strokeCircle(scale * 0.5, -scale * 0.2, scale * 0.55);
      g.strokeTriangle(-scale * 1.05, scale * 0.15, scale * 1.05, scale * 0.15, 0, scale * 1.05);
      stroke(color, 1, 0.9);
      g.strokeCircle(-scale * 0.5, -scale * 0.2, scale * 0.55);
      g.strokeCircle(scale * 0.5, -scale * 0.2, scale * 0.55);
      g.strokeTriangle(-scale * 1.05, scale * 0.15, scale * 1.05, scale * 0.15, 0, scale * 1.05);
      break;
    }
    case 'gem': {
      // Gemme facettée (hex + pointe haut)
      const w = HALF * 0.7;
      const h = HALF * 1.0;
      const pts = [
        { x: 0, y: -h },
        { x: w, y: 0 },
        { x: w * 0.5, y: h },
        { x: 0, y: h * 0.6 },
        { x: -w * 0.5, y: h },
        { x: -w, y: 0 },
      ];
      fill(color);
      g.beginPath();
      g.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
      g.closePath();
      g.fillPath();
      stroke(outline, 1.5, 1);
      g.beginPath();
      g.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
      g.closePath();
      g.strokePath();
      stroke(color, 1, 0.9);
      g.beginPath();
      g.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
      g.closePath();
      g.strokePath();
      break;
    }
  }

  return g;
}

/**
 * Crée un pion à partir d'une texture (image de voiture uploadée). Pas de teinte : l'image s'affiche telle quelle.
 * @param displaySize diamètre cible en unités monde (proportionnel à la case, pas à la résolution source).
 */
export function createCarTokenFromImage(
  scene: Phaser.Scene,
  x: number,
  y: number,
  _color: number,
  imageKey: string,
  displaySize: number
): Phaser.GameObjects.Sprite {
  const sprite = scene.add.sprite(x, y, imageKey);
  sprite.setDepth(10);
  const tex = scene.textures.get(imageKey);
  if (tex) {
    tex.setFilter(Phaser.Textures.FilterMode.LINEAR);
  }
  const w = Math.max(1, sprite.width);
  const h = Math.max(1, sprite.height);
  const scale = displaySize / Math.max(w, h);
  sprite.setScale(scale);
  sprite.setOrigin(0.5, 0.5);
  return sprite;
}

/** Met à jour l'échelle d'un pion (animation ou changement de case). */
export function applyTokenDisplaySize(
  token: Phaser.GameObjects.Sprite | Phaser.GameObjects.Graphics,
  displaySize: number
): void {
  if (token instanceof Phaser.GameObjects.Sprite) {
    const w = Math.max(1, token.width);
    const h = Math.max(1, token.height);
    token.setScale(displaySize / Math.max(w, h));
    if (token.input?.enabled) applyTokenHitArea(token);
    return;
  }
  token.setScale(displaySize / GRAPHICS_TOKEN_DIAMETER);
  if (token.input?.enabled) applyTokenHitArea(token);
}

/** Couleur utilisée pour l'aperçu du pion dans la liste de sélection */
const PREVIEW_COLOR = 0x6366f1;

/**
 * Dessine un aperçu du pion sur un contexte Canvas 2D (pour la liste de sélection).
 * Centre du dessin : (centerX, centerY). Taille proportionnée à size (demi-taille).
 */
export function drawTokenPreview(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  style: TokenCarStyle,
  size: number
): void {
  const H = size;
  const outline = '#1e293b';
  const fillColor = '#' + PREVIEW_COLOR.toString(16).padStart(6, '0');
  const dark = '#0f172a';

  ctx.save();
  ctx.translate(centerX, centerY);

  const fill = (hex: string) => {
    ctx.fillStyle = hex;
  };
  const stroke = (hex: string, width: number) => {
    ctx.strokeStyle = hex;
    ctx.lineWidth = width;
  };
  const path = (pts: { x: number; y: number }[]) => {
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
    ctx.closePath();
  };

  switch (style) {
    case 'ludo': {
      const r = H * 0.95;
      fill(fillColor);
      ctx.beginPath();
      ctx.arc(0, 0, r, 0, Math.PI * 2);
      ctx.fill();
      stroke(outline, 1.5);
      ctx.stroke();
      fill(dark);
      ctx.beginPath();
      ctx.arc(-r * 0.3, -r * 0.15, r * 0.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(r * 0.3, -r * 0.15, r * 0.2, 0, Math.PI * 2);
      ctx.fill();
      stroke(dark, 1.8);
      ctx.beginPath();
      ctx.arc(0, r * 0.2, r * 0.4, (-140 * Math.PI) / 180, (-40 * Math.PI) / 180, false);
      ctx.stroke();
      break;
    }
    case 'racing-stripe': {
      const w = H * 0.6;
      const len = H * 1.1;
      fill(fillColor);
      ctx.beginPath();
      ctx.roundRect(-w, -len, w * 2, len * 2, 5);
      ctx.fill();
      fill(dark);
      ctx.fillRect(-w * 0.25, -len, w * 0.5, len * 2);
      stroke(outline, 1.5);
      ctx.beginPath();
      ctx.roundRect(-w, -len, w * 2, len * 2, 5);
      ctx.stroke();
      break;
    }
    case 'sheriff-star': {
      const outer = H * 0.95;
      const inner = H * 0.4;
      const pts: { x: number; y: number }[] = [];
      for (let i = 0; i < 10; i++) {
        const radius = i % 2 === 0 ? outer : inner;
        const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
        pts.push({ x: Math.cos(a) * radius, y: Math.sin(a) * radius });
      }
      fill(fillColor);
      path(pts);
      ctx.fill();
      stroke(outline, 1.5);
      path(pts);
      ctx.stroke();
      break;
    }
    case 'bolt': {
      const w = H * 0.5;
      const h = H * 1.0;
      const pts = [
        { x: 0, y: -h },
        { x: w * 0.6, y: 0 },
        { x: -w * 0.3, y: 0 },
        { x: w * 0.5, y: h },
        { x: 0, y: h * 0.4 },
        { x: -w * 0.5, y: -h * 0.3 },
        { x: 0, y: -h },
      ];
      fill(fillColor);
      path(pts);
      ctx.fill();
      stroke(outline, 1.5);
      path(pts);
      ctx.stroke();
      break;
    }
    case 'shield': {
      const w = H * 0.85;
      const h = H * 1.05;
      const pts = [
        { x: 0, y: -h * 0.9 },
        { x: w, y: -h * 0.2 },
        { x: w * 0.85, y: h * 0.7 },
        { x: 0, y: h },
        { x: -w * 0.85, y: h * 0.7 },
        { x: -w, y: -h * 0.2 },
      ];
      fill(fillColor);
      path(pts);
      ctx.fill();
      stroke(outline, 1.5);
      path(pts);
      ctx.stroke();
      break;
    }
    case 'rocket': {
      const w = H * 0.55;
      const len = H * 1.1;
      fill(fillColor);
      ctx.beginPath();
      ctx.moveTo(0, -len);
      ctx.lineTo(-w, len * 0.7);
      ctx.lineTo(w, len * 0.7);
      ctx.closePath();
      ctx.fill();
      fill(dark);
      ctx.beginPath();
      ctx.moveTo(-w * 0.7, len * 0.5);
      ctx.lineTo(-w, len * 0.95);
      ctx.lineTo(-w * 0.3, len * 0.7);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(w * 0.7, len * 0.5);
      ctx.lineTo(w, len * 0.95);
      ctx.lineTo(w * 0.3, len * 0.7);
      ctx.closePath();
      ctx.fill();
      stroke(outline, 1.5);
      ctx.beginPath();
      ctx.moveTo(0, -len);
      ctx.lineTo(-w, len * 0.7);
      ctx.lineTo(w, len * 0.7);
      ctx.closePath();
      ctx.stroke();
      break;
    }
    case 'heart': {
      const scale = H * 0.9;
      fill(fillColor);
      ctx.beginPath();
      ctx.arc(-scale * 0.5, -scale * 0.2, scale * 0.55, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(scale * 0.5, -scale * 0.2, scale * 0.55, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(-scale * 1.05, scale * 0.15);
      ctx.lineTo(scale * 1.05, scale * 0.15);
      ctx.lineTo(0, scale * 1.05);
      ctx.closePath();
      ctx.fill();
      stroke(outline, 1.5);
      ctx.beginPath();
      ctx.arc(-scale * 0.5, -scale * 0.2, scale * 0.55, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(scale * 0.5, -scale * 0.2, scale * 0.55, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(-scale * 1.05, scale * 0.15);
      ctx.lineTo(scale * 1.05, scale * 0.15);
      ctx.lineTo(0, scale * 1.05);
      ctx.closePath();
      ctx.stroke();
      break;
    }
    case 'gem': {
      const w = H * 0.7;
      const h = H * 1.0;
      const pts = [
        { x: 0, y: -h },
        { x: w, y: 0 },
        { x: w * 0.5, y: h },
        { x: 0, y: h * 0.6 },
        { x: -w * 0.5, y: h },
        { x: -w, y: 0 },
      ];
      fill(fillColor);
      path(pts);
      ctx.fill();
      stroke(outline, 1.5);
      path(pts);
      ctx.stroke();
      break;
    }
  }

  ctx.restore();
}
